import React from "react";
import { Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useRouteLoaderData } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Legend, Rectangle, Tooltip, XAxis, YAxis } from "recharts";

import { useAppApi } from "@/App";
import { GameType } from "@/const";
import { tmrPushEvent } from "@components/Counters";
import { Awesome } from "@components/Helpers";
import { CommonCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

export default function StatisticsOnlines({ gameId, header }) {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:game:statistics:onlines", { game_id: gameId }));
    const commonInfo = useRouteLoaderData("commonInfo");
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    if (!apiData || !gameCommonInfo) return;
    const url_type = gameCommonInfo.game.type === GameType.DOWNLOAD ? "p2p_vkp" : "f2p_vkp";

    tmrPushEvent("game_stat_click", null, commonInfo.auth_info.user_id, gameId);

    // Функция для отправки события при показе тултипа
    const handleTooltipShow = (tooltipName) => {
        tmrPushEvent("game_stat_info_hover", tooltipName, commonInfo.auth_info.user_id, gameId);
    };

    // Функция для отправки события при клике на "Подробнее"
    const handleDetailsClick = (tooltipName) => {
        tmrPushEvent("game_stat_info_click", tooltipName, commonInfo.auth_info.user_id, gameId);
    };

    const chartData = [];
    for (const [k, v] of Object.entries(apiData.game_stats)) {
        const ks = k.toString();
        if (ks.startsWith("cnt_")) {
            const ksp = ks.split("_");
            const h = parseInt(ksp[1]);
            chartData.push({
                name: `${h - 1}-${h} ч.`,
                "Распределение пользователей по времени игры": v,
            });
        }
    }

    const hourMin = (mms) => {
        const mm = parseInt(mms);
        if (!mm) return;
        const h = Math.floor(mm / 60);
        const m = mm - h * 60;
        return h > 0 ? `${h} ${t("ч.")} ${m} ${t("м.")}` : `${m} ${t("м.")}`;
    };

    const renderCustomLegend = () => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span className="mr-1">{t("Распределение пользователей по времени игры")}</span>
            <Awesome
                type="question-circle"
                color="#2d7dd0"
                tooltip_hide={500}
                onTooltipShow={() => handleTooltipShow("chart_distribution_tooltip")}
                tooltip={
                    <>
                        {t(
                            "Показывает, как игроки распределены по времени игры (например, 0–1 ч, 1–2 ч и т. д.). Учитываются только игроки с сессией более 1 минуты. Обновляется ежедневно."
                        )}{" "}
                        <a
                            href={`https://documentation.vkplay.ru/${url_type}/f2p_game_time_statistics/#users_bucketed_by_time_played`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDetailsClick("chart_distribution_tooltip");
                            }}
                        >
                            {t("Подробнее")}
                        </a>
                    </>
                }
            />
        </div>
    );
    return (
        <CommonCard header={header}>
            <div className="dmg-callout dmg-callout-info">
                🕒 <b>{t("Статистика времени игры — что важно знать")}</b>
                <br />
                <b>{t("Обновление данных:")}</b> {t("ежедневно с 09:30 до 11:00 по МСК")}
                <br />
                <b>{t("Сессия")} :</b> {t("учитывается, если игрок провёл в игре более 1 минуты")}
                <br />
                <b>{t("Типы игр:")}</b> {t("клиентские (P2P / F2P), браузерные (включая мобильные)")}
                <br />
                <b>{t("Данные доступны с:")}</b> {t("1 января 2023 года")}
            </div>
            <Table bordered size="sm" style={{ width: "800px" }}>
                <tbody>
                    <tr>
                        <th style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            {t("Ежедневные пользователи, запустившие игру")}
                            <Awesome
                                type="question-circle"
                                color="#2d7dd0"
                                tooltip_hide={500}
                                onTooltipShow={() => handleTooltipShow("dau_tooltip")}
                                tooltip={
                                    <>
                                        {t(
                                            "Количество уникальных пользователей, запустивших игру за предыдущие сутки. Учитываются только игроки с сессией более 1 минуты."
                                        )}{" "}
                                        <a
                                            href={`https://documentation.vkplay.ru/${url_type}/p2p_game_time_statistics#daily_users_who_launched_game`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDetailsClick("dau_tooltip");
                                            }}
                                        >
                                            {t("Подробнее")}
                                        </a>
                                    </>
                                }
                            />
                        </th>
                        <td>{apiData.game_stats.dau}</td>
                    </tr>
                    <tr>
                        <th style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            {t("Количество уникальных пользователей")}
                            <Awesome
                                type="question-circle"
                                color="#2d7dd0"
                                tooltip_hide={500}
                                onTooltipShow={() => handleTooltipShow("uniqie_players_count_tooltip")}
                                tooltip={
                                    <>
                                        {t(
                                            "Число игроков, запустивших игру хотя бы раз. Не включает удалённые установки. Учитываются только игроки с сессией более 1 минуты. Обновляется ежедневно."
                                        )}{" "}
                                        <a
                                            href={`https://documentation.vkplay.ru/${url_type}/f2p_game_time_statistics/#lifetime_users_measured`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDetailsClick("uniqie_players_count_tooltip");
                                            }}
                                        >
                                            {t("Подробнее")}
                                        </a>
                                    </>
                                }
                            />
                        </th>
                        <td>{apiData.game_stats.lum}</td>
                    </tr>
                    <tr>
                        <th style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            {t("Среднее время игры")}
                            <Awesome
                                type="question-circle"
                                color="#2d7dd0"
                                tooltip_hide={500}
                                onTooltipShow={() => handleTooltipShow("avg_time_tooltip")}
                                tooltip={
                                    <>
                                        {t(
                                            "Показывает среднее время игры среди активных игроков. Обновляется ежедневно."
                                        )}{" "}
                                        <a
                                            href={`https://documentation.vkplay.ru/${url_type}/f2p_game_time_statistics/#average_time_played`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDetailsClick("avg_time_tooltip");
                                            }}
                                        >
                                            {t("Подробнее")}
                                        </a>
                                    </>
                                }
                            />
                        </th>
                        <td>{hourMin(apiData.game_stats.avg_ts)}</td>
                    </tr>
                    <tr>
                        <th style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            {t("Медианное время игры")}
                            <Awesome
                                type="question-circle"
                                color="#2d7dd0"
                                tooltip_hide={500}
                                onTooltipShow={() => handleTooltipShow("median_time_tooltip")}
                                tooltip={
                                    <>
                                        {t(
                                            "Показывает «типичное» время игры среди активных игроков. Обновляется ежедневно."
                                        )}{" "}
                                        <a
                                            href={`https://documentation.vkplay.ru/${url_type}/f2p_game_time_statistics/#median_time_played`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDetailsClick("median_time_tooltip");
                                            }}
                                        >
                                            {t("Подробнее")}
                                        </a>
                                    </>
                                }
                            />
                        </th>
                        <td>{hourMin(apiData.game_stats.med_ts)}</td>
                    </tr>
                    <tr>
                        <th
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                minHeight: "55px",
                            }}
                        >
                            {t("Диапазон времени игры")}
                            <Awesome
                                type="question-circle"
                                color="#2d7dd0"
                                tooltip_hide={700}
                                onTooltipShow={() => handleTooltipShow("time_range_tooltip")}
                                tooltip={
                                    <>
                                        {t(
                                            "Показывает разброс времени игры: от минимального до максимального. Учитываются только активные игроки. Обновляется ежедневно."
                                        )}{" "}
                                        <a
                                            href={`https://documentation.vkplay.ru/${url_type}/f2p_game_time_statistics/#time_played_range`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDetailsClick("time_range_tooltip");
                                            }}
                                        >
                                            {t("Подробнее")}
                                        </a>
                                    </>
                                }
                            />
                        </th>
                        <td>
                            {hourMin(apiData.game_stats.min_ts)} - {hourMin(apiData.game_stats.max_ts)}
                            <br />
                            {hourMin(apiData.game_stats.std_dev)} {t("(стандартное отклонение времени игры)")}
                        </td>
                    </tr>
                </tbody>
            </Table>
            <Table bordered size="sm" style={{ width: "500px" }}>
                <tbody>
                    <tr>
                        <th>
                            {t("Минимальное время игры  ")}
                            <Awesome
                                type="question-circle"
                                color="#2d7dd0"
                                tooltip_hide={700}
                                onTooltipShow={() => handleTooltipShow("min_time_tooltip")}
                                tooltip={
                                    <>
                                        {t(
                                            "Показывает, какой процент игроков достиг порога времени в игре (например, 30 минут, 1 час и т. д.). Учитываются только активные игроки. Обновляется ежедневно."
                                        )}{" "}
                                        <a
                                            href={`https://documentation.vkplay.ru/${url_type}/f2p_game_time_statistics/#minimum_time_played`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDetailsClick("min_time_tooltip");
                                            }}
                                        >
                                            {t("Подробнее")}
                                        </a>
                                    </>
                                }
                            />
                        </th>
                        <th>{t("Процент пользователей")}</th>
                    </tr>
                    {Object.entries(apiData.perc_stats)
                        .sort(([a], [b]) => {
                            const asp = a.split("_");
                            const bsp = b.split("_");
                            return Number(asp[1]) - Number(bsp[1]);
                        })
                        .map((d) => {
                            const k = d[0].toString();
                            const ksp = k.split("_");
                            return (
                                <tr key={k}>
                                    <td>{hourMin(ksp[1])}</td>
                                    <td>{d[1]}</td>
                                </tr>
                            );
                        })}
                </tbody>
            </Table>
            <div>
                <BarChart width={800} height={300} data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-90} height={80} textAnchor="end" />
                    <YAxis />
                    <Tooltip />
                    <Legend content={renderCustomLegend} />
                    <Bar
                        dataKey={t("Распределение пользователей по времени игры")}
                        fill="#8884d8"
                        barSize="10"
                        activeBar={<Rectangle fill="pink" stroke="blue" />}
                    />
                </BarChart>
            </div>
        </CommonCard>
    );
}
